import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Grade } from '../../types';
import { settingsService } from '../../services/settingsService';

// Default grading system
const DEFAULT_GRADES: Omit<Grade, '_id' | 'schoolId' | 'createdAt' | 'updatedAt'>[] = [
  {
    name: 'A+',
    minPercentage: 80,
    maxPercentage: 100,
    color: '#2e7d32',
    isActive: true,
  },
  {
    name: 'A',
    minPercentage: 70,
    maxPercentage: 79,
    color: '#2196f3',
    isActive: true,
  },
  {
    name: 'B',
    minPercentage: 60,
    maxPercentage: 69,
    color: '#4c00b0',
    isActive: true,
  },
  {
    name: 'C',
    minPercentage: 50,
    maxPercentage: 59,
    color: '#fbc02d',
    isActive: true,
  },
  {
    name: 'F',
    minPercentage: 0,
    maxPercentage: 49,
    color: '#d32f2f',
    isActive: true,
  },
];

interface GradesState {
  grades: Grade[];
  loading: boolean;
  error: string | null;
}

const initialState: GradesState = {
  grades: [],
  loading: false,
  error: null,
};

// Fetch grades from API
export const fetchGrades = createAsyncThunk(
  'grades/fetchGrades',
  async (schoolId?: string) => {
    const response = await settingsService.getGrades({
      isActive: true,
      limit: 50,
      schoolId,
    });
    return response.data?.grades || [];
  }
);

const gradesSlice = createSlice({
  name: 'grades',
  initialState,
  reducers: {
    setDefaultGrades: (state) => {
      // Convert default grades to Grade format with mock IDs
      state.grades = DEFAULT_GRADES.map((grade, index) => ({
        ...grade,
        _id: `default-${index}`,
        schoolId: {
          _id: '',
          name: '',
        },
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      })) as Grade[];
    },
    clearGrades: (state) => {
      state.grades = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchGrades.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchGrades.fulfilled, (state, action: PayloadAction<Grade[]>) => {
        state.loading = false;
        // If no grades exist, use defaults
        if (action.payload.length === 0) {
          state.grades = DEFAULT_GRADES.map((grade, index) => ({
            ...grade,
            _id: `default-${index}`,
            schoolId: {
              _id: '',
              name: '',
            },
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          })) as Grade[];
        } else {
          state.grades = action.payload;
        }
      })
      .addCase(fetchGrades.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch grades';
        // On error, use defaults
        state.grades = DEFAULT_GRADES.map((grade, index) => ({
          ...grade,
          _id: `default-${index}`,
          schoolId: {
            _id: '',
            name: '',
          },
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })) as Grade[];
      });
  },
});

export const { setDefaultGrades, clearGrades } = gradesSlice.actions;
export default gradesSlice.reducer;

